import { createClient, getBaseUrl } from 'shared/apollo/client';
import { getMyAccount } from 'shared/apollo/provider';
import {
  type FeatureFlags,
  normalizeFeatures,
} from 'shared/providers/FeatureFlags/useFeatureFlags';
import { type GhostPartner } from 'shared/utils/partnerUser';
import { type CnReport, type CnTask, type ReportFormData } from './types';

export type PartnerReportStatus =
  | 'has_tasks'
  | 'in_progress'
  | 'published'
  | 'completed'
  | 'must_create'
  | 'has_reports'
  | 'select_or_create'
  | '';

/** Payload returned by /v4/auth/<partner>-login and the ghost-login endpoint. */
export interface PartnerLoginData {
  ti_token: string;
  ti_user_id?: number;
  ti_account_id?: number;
  email?: string;
  name?: string;
  report_status?: PartnerReportStatus;
  report_id?: number | null;
  tasks?: CnTask[];
  reports?: CnReport[];
  cn_opted_in_count?: number;
}

export const CN_OPT_IN_LIMIT = 800;
export const TODO_URL = '/npo/todo/by-due-date';
export const PROGRAMS_AND_REPORTS_URL = '/npo/programs-and-reports';
export const reportBuilderUrl = (reportId: number | null | undefined) =>
  `/npo/report-builder/report/${reportId}`;
export const publishedReportUrl = (reportId: number) => `/report/${reportId}`;

// ---------------------------------------------------------------------------
// Session (localStorage) helpers
// ---------------------------------------------------------------------------

const SESSION_KEYS = ['cn_ti_token', 'cn_user_data', 'bb_partner'] as const;

export const clearPartnerSession = () =>
  SESSION_KEYS.forEach(key => localStorage.removeItem(key));

/**
 * Store the TrueImpact token + user data so the rest of the app (ApolloAndUserProvider)
 * can authenticate as the partner user. Never call this when ghosting: the
 * Cognito ghost session must stay intact.
 */
export const persistPartnerSession = (
  data: PartnerLoginData,
  partner: GhostPartner,
  features?: FeatureFlags,
) => {
  localStorage.setItem('cn_ti_token', data.ti_token);
  if (partner === 'bb') localStorage.setItem('bb_partner', 'true');
  if (data.ti_user_id) {
    localStorage.setItem(
      'cn_user_data',
      JSON.stringify({
        userId: data.ti_user_id,
        accountId: data.ti_account_id,
        email: data.email,
        firstName: data.name,
        lastName: '',
        features,
      }),
    );
  }
};

// ---------------------------------------------------------------------------
// Backend calls
// ---------------------------------------------------------------------------

const jsonHeaders = (tiToken?: string): HeadersInit => ({
  'Content-Type': 'application/json',
  ...(tiToken ? { 'TI-ID-TOKEN': tiToken } : {}),
});

/** Exchange a partner (Auth0 / Blackbaud) token for a TrueImpact session. */
export const fetchPartnerLogin = async (
  partner: GhostPartner,
  partnerToken: string,
  errorForStatus: (status: number) => string,
): Promise<PartnerLoginData> => {
  const response = await fetch(`${getBaseUrl()}/v4/auth/${partner}-login`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ [`${partner}_token`]: partnerToken }),
  });
  if (!response.ok) throw new Error(errorForStatus(response.status));
  const data: PartnerLoginData = await response.json();
  if (!data?.ti_token) throw new Error('Invalid response');
  return data;
};

/**
 * Feature flags live on `myAccount`, which the widget cannot read through
 * `useFeatureFlags` because it renders outside the authenticated UserProvider.
 */
export const fetchAccountFeatures = async (
  tiToken: string,
): Promise<FeatureFlags> => {
  const { data } = await getMyAccount(createClient({ 'ti-id-token': tiToken }));
  return normalizeFeatures(data?.myAccount?.[0]?.features);
};

const cnReportRequest = async <T>(
  tiToken: string,
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
  failureMessage: string,
): Promise<T> => {
  const response = await fetch(`${getBaseUrl()}/v4/auth/cn-report/${path}`, {
    method,
    headers: jsonHeaders(tiToken),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(failureMessage);
  return response.json();
};

export const selectCnReport = (tiToken: string, reportId: number) =>
  cnReportRequest(
    tiToken,
    'select',
    'POST',
    { report_id: reportId },
    'Failed to select report',
  );

export const createCnReport = (tiToken: string, form: ReportFormData) =>
  cnReportRequest<{ report: CnReport }>(
    tiToken,
    'create',
    'POST',
    form,
    'Failed to create report',
  );

export const updateCnReport = (
  tiToken: string,
  reportId: number,
  form: ReportFormData,
) =>
  cnReportRequest<{ report: CnReport }>(
    tiToken,
    `${reportId}/update`,
    'PUT',
    form,
    'Failed to update report',
  );

// ---------------------------------------------------------------------------
// Login payload -> what the widget should do once the user enters
// ---------------------------------------------------------------------------

const ONBOARDING_TASK_TYPES = ['add_programs', 'complete_profile', 'create_report'];

export const isOnboarding = (tasks: CnTask[]) =>
  tasks.length > 0 &&
  tasks.every(t => ONBOARDING_TASK_TYPES.includes(t.task_type));

export const hasProgramsOrReports = (data: PartnerLoginData): boolean =>
  (data.reports?.length ?? 0) > 0 ||
  (data.tasks ?? []).some(t => t.program_id != null);

export type PartnerEntry =
  | { kind: 'redirect'; href: string }
  | { kind: 'tasks'; tasks: CnTask[] }
  | { kind: 'published' | 'completed'; reportId: number | null };

/** Shared by the Blackbaud widget and the CN task-updates widget. */
export const resolvePartnerEntry = (data: PartnerLoginData): PartnerEntry => {
  const tasks = data.tasks ?? [];
  switch (data.report_status) {
    case 'has_tasks':
      return isOnboarding(tasks)
        ? { kind: 'redirect', href: TODO_URL }
        : { kind: 'tasks', tasks };
    case 'in_progress':
      return { kind: 'redirect', href: reportBuilderUrl(data.report_id) };
    case 'published':
    case 'completed':
      return { kind: data.report_status, reportId: data.report_id ?? null };
    default:
      // must_create / fallback
      return { kind: 'redirect', href: PROGRAMS_AND_REPORTS_URL };
  }
};
